import React, { useState, useRef, useEffect } from "react";

const ChatBot = ({ onClose }) => {
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Hi 👋 Welcome to INSD! Discover excellence in design education. How can I help you today?",
      timestamp: new Date(),
    },
  ]);

  const [input, setInput] = useState("");
  const [step, setStep] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationFlow, setConversationFlow] = useState("initial");
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    course: "",
    centre: "",
  });
  const [currentDetail, setCurrentDetail] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const messagesEndRef = useRef(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch courses on component mount
  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/courses/get-all`
      );
      const data = await response.json();

      if (data.success && data.data) {
        setCourses(data.data);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const simulateTyping = (responseMessages, callback) => {
    setIsTyping(true);
    let currentIndex = 0;

    const typeNextMessage = () => {
      if (currentIndex < responseMessages.length) {
        setMessages((prev) => [...prev, responseMessages[currentIndex]]);
        currentIndex++;
        setTimeout(typeNextMessage, 800);
      } else {
        setIsTyping(false);
        if (callback) callback();
      }
    };

    setTimeout(typeNextMessage, 600);
  };

  const handleContactInfo = () => {
    setIsTyping(true);
    setTimeout(() => {
      const botResponses = [
        {
          from: "bot",
          text: "Here are our contact details. We'd love to hear from you! 📞",
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `📍 **Address:**\nINSD Campus, Design Avenue, Bhubaneswar`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `📞 **Phone:**\n+91-22-1234-5678\n+91-22-8765-4321`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: `✉️ **Email:**\ninfo@insd.edu.in`,
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: "🕒 **Office Hours:**\n• Monday-Friday: 9 AM - 6 PM\n• Saturday: 10 AM - 4 PM\n• Sunday: Closed",
          timestamp: new Date(),
        },
        {
          from: "bot",
          text: "Feel free to reach out to us for any questions, course inquiries, or admissions! We're always happy to help. 😊",
          timestamp: new Date(),
        },
      ];
      simulateTyping(botResponses, () => {
        setStep(1);
        setConversationFlow("free_chat");
      });
    }, 800);
  };

  const startCourseInquiry = (courseName, courseId, category) => {
    setSelectedCourseId(courseId);
    setSelectedCourse(courseName);
    setSelectedCategory(category);
    setIsTyping(true);
    setTimeout(() => {
      const botResponses = [
        {
          from: "bot",
          text: `Great choice! Let's get you more information about "${category}". I'll need a few details to assist you better. Please enter your name.`,
          timestamp: new Date(),
        },
      ];
      simulateTyping(botResponses, () => {
        setConversationFlow("collecting_details");
        setCurrentDetail("name");
      });
    }, 800);
  };

  const handleInquiryDetails = (text) => {
    const updatedDetails = { ...userDetails };

    switch (currentDetail) {
      case "name":
        updatedDetails.name = text;
        setUserDetails(updatedDetails);
        setCurrentDetail("phone");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Thanks! What's your phone number?",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "phone":
        const phoneRegex = /[\d\s\-\(\)\+]{10,}/;
        if (!phoneRegex.test(text.replace(/\D/g, ""))) {
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text: "Please enter a valid phone number (at least 10 digits):",
              timestamp: new Date(),
            },
          ]);
          return;
        }
        updatedDetails.phone = text;
        setUserDetails(updatedDetails);
        setCurrentDetail("email");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Perfect! What's your email address?",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(text)) {
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text: "Please enter a valid email address:",
              timestamp: new Date(),
            },
          ]);
          return;
        }
        updatedDetails.email = text;
        setUserDetails(updatedDetails);
        setCurrentDetail("centre");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Which centre are you interested in? Please choose: Pune, Andheri, or Bhubaneswar",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "centre":
        // Normalize centre name to lowercase to match enum
        updatedDetails.centre = text.toLowerCase().trim();

        // Validate against allowed centres
        const allowedCentres = ["pune", "andheri", "bhubaneswar"];
        if (!allowedCentres.includes(updatedDetails.centre)) {
          setMessages((prev) => [
            ...prev,
            {
              from: "bot",
              text: "Please enter a valid centre name: Pune, Andheri, or Bhubaneswar",
              timestamp: new Date(),
            },
          ]);
          return;
        }

        setUserDetails(updatedDetails);
        setCurrentDetail("message");
        setIsTyping(true);
        setTimeout(() => {
          const botResponses = [
            {
              from: "bot",
              text: "Almost done! Any specific questions or message for us? (Optional - type 'skip' to skip)",
              timestamp: new Date(),
            },
          ];
          simulateTyping(botResponses);
        }, 800);
        break;

      case "message":
        if (text.toLowerCase() !== "skip") {
          updatedDetails.message = text;
        } else {
          updatedDetails.message = "I want to know more about this course.";
        }
        setUserDetails(updatedDetails);
        completeInquiry(updatedDetails);
        break;

      default:
        break;
    }
  };

  const completeInquiry = async (details) => {
    setIsTyping(true);

    try {
      const inquiryData = {
        name: details.name,
        email: details.email,
        phone: details.phone,
        message: details.message || "I want to know more about this course.",
        course: selectedCourseId,
        centre: details.centre,
      };

      console.log("Submitting inquiry data:", inquiryData); // Debug log

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/contact/create`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(inquiryData),
        }
      );

      const data = await response.json();
      console.log("Response from server:", data); // Debug log

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit inquiry");
      }

      setTimeout(() => {
        const botResponses = [
          {
            from: "bot",
            text: "🎉 Your inquiry has been submitted successfully!",
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: `📋 **Inquiry Summary:**\n📚 **Course:** ${selectedCourse}\n🏷️ **Category:** ${selectedCategory}\n👤 **Name:** ${
              details.name
            }\n📞 **Phone:** ${details.phone}\n📧 **Email:** ${
              details.email
            }\n📍 **Centre:** ${
              details.centre.charAt(0).toUpperCase() + details.centre.slice(1)
            }${details.message ? `\n💬 **Message:** ${details.message}` : ""}`,
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: "Our admissions team will contact you shortly with detailed information about the course. We're excited to help you start your design journey! 🎨",
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: "For immediate assistance:\n📍 Visit us: INSD Campus, Design Avenue Bhubaneswar\n📞 Call: +91-22-1234-5678\n✉️ Email: info@insd.edu.in",
            timestamp: new Date(),
          },
        ];
        simulateTyping(botResponses, () => {
          setConversationFlow("free_chat");
          setStep(1);

          setUserDetails({
            name: "",
            email: "",
            phone: "",
            message: "",
            course: "",
            centre: "",
          });
          setSelectedCourse("");
          setSelectedCourseId("");
          setSelectedCategory("");
        });
      }, 800);
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      setTimeout(() => {
        const errorResponses = [
          {
            from: "bot",
            text: `Oops! There was an issue processing your inquiry: ${error.message}. Please try again or contact us directly.`,
            timestamp: new Date(),
          },
          {
            from: "bot",
            text: "📞 Call: +91-22-1234-5678\n✉️ Email: info@insd.edu.in",
            timestamp: new Date(),
          },
        ];
        simulateTyping(errorResponses, () => {
          setConversationFlow("free_chat");
          setStep(1);
        });
      }, 800);
    }
  };

  const handleFreeMessage = (userMessage) => {
    setIsTyping(true);
    const userMessageLower = userMessage.toLowerCase();

    setTimeout(() => {
      let botResponse = "";

      if (
        userMessageLower.includes("hour") ||
        userMessageLower.includes("time") ||
        userMessageLower.includes("open")
      ) {
        botResponse =
          "Our office hours are:\n• Monday-Friday: 9 AM - 6 PM\n• Saturday: 10 AM - 4 PM\n• Sunday: Closed\n\nYou can visit us anytime during these hours for course consultations! 🕒";
      } else if (
        userMessageLower.includes("fee") ||
        userMessageLower.includes("cost") ||
        userMessageLower.includes("price")
      ) {
        botResponse =
          "Our course fees vary based on the program:\n• Diploma courses: Starting from ₹1,50,000\n• Degree programs: Starting from ₹3,00,000\n• Certificate courses: Starting from ₹50,000\n\nFor exact fee details and payment plans, our admissions team can provide a customized quote! 💰";
      } else if (
        userMessageLower.includes("admission") ||
        userMessageLower.includes("eligibility") ||
        userMessageLower.includes("qualification")
      ) {
        botResponse =
          "Admission requirements vary by course:\n• Diploma: 10+2 or equivalent\n• Degree: 10+2 with minimum 50%\n• Certificate: 10th pass\n\nWe also offer portfolio-based admissions for some programs. Would you like to know about a specific course? 🎓";
      } else if (
        userMessageLower.includes("course") ||
        userMessageLower.includes("program")
      ) {
        botResponse =
          "We offer a wide range of design and technology courses. Would you like to browse our available programs? Just type 'Course Inquiry' or select it from the menu! 🎨";
      } else if (
        userMessageLower.includes("placement") ||
        userMessageLower.includes("job") ||
        userMessageLower.includes("career")
      ) {
        botResponse =
          "We have an excellent placement record! 📈\n• 85%+ placement rate\n• Top companies recruit from INSD\n• Average starting package: ₹3-5 LPA\n• Career counseling and portfolio building support\n\nOur alumni work with leading design studios and brands! 💼";
      } else if (
        userMessageLower.includes("facility") ||
        userMessageLower.includes("infrastructure") ||
        userMessageLower.includes("campus")
      ) {
        botResponse =
          "Our state-of-the-art facilities include:\n• Modern design labs\n• Mac labs with latest software\n• Fashion workshop\n• Photography studio\n• Library with extensive design resources\n• Student exhibition space\n\nWe provide everything you need for a great learning experience! 🏫";
      } else if (
        userMessageLower.includes("thank") ||
        userMessageLower.includes("thanks")
      ) {
        botResponse =
          "You're welcome! 😊 Is there anything else I can help you with today?";
      } else if (
        userMessageLower.includes("hello") ||
        userMessageLower.includes("hi") ||
        userMessageLower.includes("hey")
      ) {
        botResponse = "Hello again! 👋 How can I assist you further?";
      } else if (
        userMessageLower.includes("bye") ||
        userMessageLower.includes("goodbye")
      ) {
        botResponse =
          "Thanks for chatting! Have a great day and we hope to see you at INSD soon! 🎓✨";
      } else {
        botResponse =
          "Thanks for your message! I'd be happy to help you with that. Our team at INSD can assist with all your design education needs. Could you tell me a bit more about what you're looking for?";
      }

      const botResponses = [
        { from: "bot", text: botResponse, timestamp: new Date() },
      ];

      if (
        !userMessageLower.includes("thank") &&
        !userMessageLower.includes("hello") &&
        !userMessageLower.includes("hi") &&
        !userMessageLower.includes("bye")
      ) {
        botResponses.push({
          from: "bot",
          text: "For immediate assistance, you can also:\n📍 Visit us: INSD Campus, Design Avenue\n📞 Call: +91-22-1234-5678\n✉️ Email: info@insd.edu.in",
          timestamp: new Date(),
        });
      }

      simulateTyping(botResponses, () => {
        setConversationFlow("free_chat");
        setStep(1);
      });
    }, 1000);
  };

  const handleSend = (text) => {
    if (!text.trim()) return;

    const userMessage = {
      from: "user",
      text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    if (conversationFlow === "collecting_details") {
      handleInquiryDetails(text);
    } else if (text === "Contact Info") {
      handleContactInfo();
    } else if (text === "Course Inquiry") {
      setIsTyping(true);
      setTimeout(() => {
        const botResponses = [
          {
            from: "bot",
            text: "Great! Here are our available courses. Select the one you're interested in:",
            timestamp: new Date(),
          },
        ];
        simulateTyping(botResponses, () => {
          setStep(2);
          setConversationFlow("course_selection");
        });
      }, 800);
    } else {
      handleFreeMessage(text);
    }

    setInput("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const resetChat = () => {
    setMessages([
      {
        from: "bot",
        text: "Hi 👋 Welcome to INSD! Discover excellence in design education. How can I help you today?",
        timestamp: new Date(),
      },
    ]);
    setStep(0);
    setConversationFlow("initial");
    setInput("");
    setUserDetails({
      name: "",
      email: "",
      phone: "",
      message: "",
      course: "",
      centre: "",
    });
    setSelectedCourse("");
    setCurrentDetail("");
    setSelectedCourseId("");
    setSelectedCategory("");
  };

  const getInputPlaceholder = () => {
    if (conversationFlow === "collecting_details") {
      switch (currentDetail) {
        case "name":
          return "Enter your full name...";
        case "email":
          return "Enter your email...";
        case "phone":
          return "Enter your phone number...";
        case "centre":
          return "Enter: Pune, Andheri, or Bhubaneswar...";
        case "message":
          return "Enter your message or type 'skip'...";
        default:
          return "Type your response...";
      }
    }
    return "Type your message or question...";
  };

  return (
    <div className="fixed bottom-20 right-6 w-80 h-[28rem] bg-white shadow-2xl rounded-xl border border-gray-200 flex flex-col z-50 font-sans">
      <div className="bg-gradient-to-r from-[#DA3335] to-[#144982] text-white px-4 py-3 rounded-t-xl flex justify-between items-center shadow-md">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span className="font-semibold text-sm">INSD Assistant</span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={resetChat}
            className="text-white hover:bg-white/20 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 text-sm font-light"
            title="Start new conversation"
          >
            ↺
          </button>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 text-lg font-light"
          >
            ×
          </button>
        </div>
      </div>

      <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
        <div className="space-y-3">
          {messages.map((msg, idx) => {
            if (!msg?.text || msg.text.trim() === "") return null;

            return (
              <div
                key={idx}
                className={`flex flex-col ${
                  msg?.from === "bot" ? "items-start" : "items-end"
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg?.from === "bot"
                      ? "bg-white border border-gray-200 text-[#1b0a0a] rounded-tl-none shadow-sm"
                      : "bg-gradient-to-r from-[#DA3335] to-[#144982] text-white rounded-tr-none shadow"
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {msg?.text}
                  </p>
                  <span
                    className={`text-xs mt-1 block ${
                      msg?.from === "bot" ? "text-[#3c3737]" : "text-gray-100"
                    }`}
                  >
                    {formatTime(msg?.timestamp)}
                  </span>
                </div>
              </div>
            );
          })}

          {isTyping && (
            <div className="flex items-start space-x-2">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            </div>
          )}

          {step === 0 && !isTyping && conversationFlow === "initial" && (
            <div className="space-y-2 mt-2">
              <p className="text-xs text-[#3c3737] font-medium mb-2">
                Choose an option or type your question directly:
              </p>
              <button
                onClick={() => handleSend("Course Inquiry")}
                className="block w-full text-left bg-white border border-gray-200 px-4 py-3 rounded-xl hover:bg-[#fef5f5] hover:border-[#DA3335] transition-all duration-200 text-sm font-medium text-[#1b0a0a] shadow-sm hover:shadow"
              >
                📚 Course Inquiry
              </button>
              <button
                onClick={() => handleSend("Contact Info")}
                className="block w-full text-left bg-white border border-gray-200 px-4 py-3 rounded-xl hover:bg-[#fef5f5] hover:border-[#DA3335] transition-all duration-200 text-sm font-medium text-[#1b0a0a] shadow-sm hover:shadow"
              >
                📞 Contact Info
              </button>
            </div>
          )}

          {step === 2 &&
            !isTyping &&
            conversationFlow === "course_selection" && (
              <div className="space-y-2 mt-2">
                <p className="text-xs text-[#3c3737] font-medium mb-2">
                  Select a course category:
                </p>
                {loading ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-[#3c3737]">Loading courses...</p>
                  </div>
                ) : (
                  <>
                    {courses.map((course) => (
                      <button
                        key={course._id}
                        onClick={() =>
                          startCourseInquiry(
                            course.name,
                            course._id,
                            course.category
                          )
                        }
                        className="block w-full text-left bg-white border border-gray-200 px-4 py-3 rounded-xl hover:bg-[#fef5f5] hover:border-[#DA3335] transition-all duration-200 text-sm shadow-sm hover:shadow"
                      >
                        <div className="font-medium text-[#1b0a0a] text-md">
                          📂 {course.category}
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}

          {conversationFlow === "free_chat" && !isTyping && (
            <div className="text-center mt-2">
              <p className="text-xs text-[#3c3737]">
                Feel free to ask more questions! 💬
              </p>
            </div>
          )}
        </div>
        <div ref={messagesEndRef} />
      </div>

      <div className="p-3 border-t border-gray-200 bg-white rounded-b-xl">
        <div className="flex space-x-2">
          <input
            type={
              currentDetail === "email"
                ? "email"
                : currentDetail === "phone"
                ? "tel"
                : "text"
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={getInputPlaceholder()}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#DA3335] focus:border-transparent transition-all text-[#1b0a0a]"
            disabled={isTyping}
          />
          <button
            onClick={() => handleSend(input)}
            disabled={!input.trim() || isTyping}
            className="bg-[#1b0a0a] text-white px-4 py-2.5 rounded-lg hover:bg-[#2d1515] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm font-medium text-sm"
          >
            Send
          </button>
        </div>
        <p className="text-xs text-[#3c3737] text-center mt-2">
          {conversationFlow === "initial"
            ? "Ask me anything about INSD!"
            : "We're here to help with all your questions!"}
        </p>
      </div>
    </div>
  );
};

export default ChatBot;
